import { type FC, Fragment, useState } from 'react';
import ActionButton from './action-button';
import { SvgIcon } from './svg-icon';
import ClickableIcon from './clickable-icon';
import type { Day, TimeRange } from '../models/repair-bookings';
import styles from '../styles/day-picker.module.scss';

const WEEK_LENGTH = 7;

const DayPicker: FC<{
    className?: string;
    dayOptions: Day[][];
    timeOptions: TimeRange[];
    isAvailable: (rawDate: string, time: TimeRange) => boolean;
    onRangeSelect: (rawDate: string, time: TimeRange) => void;
}> = ({ className = '', dayOptions, timeOptions, isAvailable, onRangeSelect }) => {
    const [dayIndex, setDayIndex] = useState(0);
    const days = dayOptions[dayIndex] ?? [];
    const displayDays = Array.from({ length: WEEK_LENGTH }, (_, index) => days[index] ?? null);
    const firstDay = days[0];
    const lastDay = days[days.length - 1];

    return (
        <div className={`${styles.dayPicker} ${className}`}>
            <div className={styles.weekSelector}>
                <ActionButton
                    className={styles.selectButton}
                    primary={false}
                    title="前の週"
                    disabled={dayIndex === 0}
                    onClick={() => setDayIndex(dayIndex - 1)}
                />
                <ActionButton
                    className={styles.selectButton}
                    primary={false}
                    title="次の週"
                    disabled={dayIndex === dayOptions.length - 1}
                    onClick={() => setDayIndex(dayIndex + 1)}
                />
            </div>
            {firstDay && lastDay && (
                <div className={styles.weekTitle}>
                    {firstDay.date}
                    {firstDay.day} ～ {lastDay.date}
                    {lastDay.day}
                </div>
            )}
            <div className={styles.dayGrid}>
                <div className={`${styles.cell} ${styles.headerCell} ${styles.cornerCell}`} />
                {displayDays.map((day, index) =>
                    day ? (
                        <div
                            key={`${day.date}-${day.day}`}
                            className={`${styles.cell} ${styles.headerCell}`}
                        >
                            <div>{`${new Date(day.rawDate).getMonth() + 1}/${new Date(day.rawDate).getDate()}`}</div>
                            <div>{day.day}</div>
                        </div>
                    ) : (
                        <div
                            key={`hidden-header-${index}`}
                            className={`${styles.cell} ${styles.headerCell} ${styles.placeholderCell}`}
                            aria-hidden="true"
                        />
                    )
                )}
                {timeOptions.map((time) => (
                    <Fragment key={`${time.start}-${time.end}`}>
                        <div
                            key={`${time.start}-${time.end}-label`}
                            className={`${styles.cell} ${styles.headerCell} ${styles.timeCell}`}
                        >
                            <div>{time.start}</div>
                            <div>～</div>
                            <div>{time.end}</div>
                        </div>
                        {displayDays.map((day, index) =>
                            day ? (
                                <div
                                    key={`${day.date}-${day.day}-${time.start}-${time.end}`}
                                    className={styles.cell}
                                >
                                    {isAvailable(day.rawDate, time) ? (
                                        <ClickableIcon
                                            iconName="circle"
                                            onClick={() => onRangeSelect(day.rawDate, time)}
                                        />
                                    ) : (
                                        <SvgIcon name="cross" />
                                    )}
                                </div>
                            ) : (
                                <div
                                    key={`hidden-cell-${time.start}-${time.end}-${index}`}
                                    className={`${styles.cell} ${styles.placeholderCell}`}
                                    aria-hidden="true"
                                />
                            )
                        )}
                    </Fragment>
                ))}
            </div>
        </div>
    );
};

export default DayPicker;
